import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ShoppingCart, Plus, Package, Users, LayoutGrid, Trash2, X, Search, Camera, Receipt, Save,
  Minus, Image as ImageIcon, BarChart3, Settings, Printer, Clock, ArrowUpRight, Edit3,
  FileText, Download, AlertTriangle, Ban, UserCheck, Share2, Copy, AlertOctagon, RefreshCw, Truck,
  RotateCcw, ChevronLeft, ChevronRight, ScanBarcode, UserCircle2, Layers, Lock, LogIn, Menu, LogOut,
  HelpCircle, CheckSquare, Square, MapPin, Phone, PauseCircle, PlayCircle, AlertCircle, Banknote,
  CreditCard, Smartphone, BookOpen, Crown, Unlock, Globe, Wallet, History, FileCheck, Boxes, PenLine,
  CreditCard as CardIcon, Store, ArrowRightLeft, UserCog, Briefcase, FileOutput, Megaphone, UserPlus,
  Link, MonitorSmartphone, Contact, FileMinus } from 'lucide-react';
import { apiCall, generateId, formatPrice, formatDate, compressImage,
  base64ToFile, printHtmlDiv, printRawHtml, printBarcodeDiv } from '../utils/utils.js';

const ShareModal = ({ product, category, products, isBulk, onClose, hidePrice, getProductPrice }) => {
    const [sharing, setSharing] = useState(false); 
    const isCategoryShare = !!category; 
    const isBulkShare = !!isBulk;
    const itemsToShare = (isCategoryShare || isBulkShare) ? (products || []) : [product]; 
    
    const displayImage = [];
    if (isCategoryShare) {
        itemsToShare.forEach(p => { 
            if (p && p.images && p.images.length > 0) displayImage.push(p.images[0]); 
        });
        displayImage.splice(15);
    }
    else if (itemsToShare.length > 0) {
        itemsToShare.slice(0, 15).forEach(p => { if (p && p.images && p.images.length > 0) displayImage.push(p.images[0]); });
    }

    const generateShareText = () => {
        if (isCategoryShare) {
            return `*Category: ${category?.name}*\n\nOrder now!`; 
        }
        if (isBulkShare) {
            if (itemsToShare.length === 1) {
                 return `📦 *${itemsToShare[0]?.name}*\n💰 Price: ${formatPrice(getProductPrice(itemsToShare[0]))}\n\nOrder now!`;
            }
            return `*Selected Products (${itemsToShare.length})*\n\nOrder now!`;
        }
        
        let text = `📦 *${product?.name}*\n`; 
        if (!hidePrice) text += `💰 Price: ${formatPrice(getProductPrice(product))}\n\n`;
        return text + `Order now!`;
    };

    const handleNativeShare = async () => {
        setSharing(true); 
        const text = generateShareText();
        if (navigator.share) {
            try {
                const filesArray = [];
                if (displayImage.length > 0) { 
                    for (let i = 0; i < displayImage.length; i++) { 
                        let filename = isCategoryShare ? `${category?.name}_${i+1}` : isBulkShare ? `Bulk_Share_${i+1}` : `${product?.name}_${i+1}`;
                        filesArray.push(await base64ToFile(displayImage[i], `${filename}.jpg`)); 
                    } 
                }
                const shareData = { title: isCategoryShare ? category?.name : isBulkShare ? 'Selected Products' : product?.name, text: text };
                if (filesArray.length > 0 && navigator.canShare && navigator.canShare({ files: filesArray })) { 
                    shareData.files = filesArray; 
                } else { 
                    shareData.url = window.location.href; 
                }
                await navigator.share(shareData);
            } catch (err) { 
                if (err.name !== 'AbortError') { 
                    navigator.clipboard.writeText(generateShareText()); 
                    alert("Copied Text (Images could not be attached)!"); 
                } 
            }
        } else { 
            navigator.clipboard.writeText(generateShareText()); 
            alert("Copied text!"); 
        }
        setSharing(false);
    };
    
    return (
        <div className="fixed inset-0 bg-[#1a1a1a]/60 backdrop-blur-sm z-[110] flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-sm rounded-xl shadow-lg border border-[#e1e3e5] overflow-hidden">
                <div className="p-4 border-b border-[#e1e3e5] flex justify-between items-center">
                    <h3 className="font-bold text-[#1a1a1a]">{isCategoryShare ? 'Share Category Photos' : isBulkShare ? `Share ${itemsToShare.length} Items` : 'Share Product'}</h3>
                    <button onClick={onClose} className="text-[#5c5f62]"><X size={20}/></button>
                </div>
                <div className="p-6">
                    <div className="flex gap-2 overflow-x-auto pb-4 mb-2 scrollbar-hide snap-x">
                        {displayImage.length > 0 ? displayImage.map((img, i) => (
                            <div key={i} className="aspect-square w-24 flex-shrink-0 bg-[#f6f7f8] rounded-md overflow-hidden border border-[#e1e3e5] snap-center">
                                <img src={img} className="w-full h-full object-contain"/>
                            </div>
                        )) : (
                            <div className="w-full aspect-square bg-[#f6f7f8] rounded-md border border-[#e1e3e5] flex items-center justify-center text-[#c9cccf]"><ImageIcon size={24} /></div>
                        )}
                    </div>
                    <h2 className="text-base font-bold mb-1 text-center text-[#1a1a1a]">
                        {isCategoryShare ? category?.name : isBulkShare ? `${itemsToShare.length} Selected Products` : product?.name}
                    </h2>
                    {!isCategoryShare && !isBulkShare && !hidePrice && product && (
                        <p className="text-[#008060] font-semibold text-sm mb-6 text-center">{formatPrice(getProductPrice(product))}</p>
                    )}
                    <div className="grid grid-cols-2 gap-3 mt-4">
                        <button onClick={handleNativeShare} disabled={sharing} className="bg-[#008060] hover:bg-[#006e52] text-white py-2 rounded-lg font-semibold flex items-center justify-center gap-2">
                            <Share2 size={16}/> Share
                        </button>
                        <button onClick={() => { navigator.clipboard.writeText(generateShareText()); alert("Copied!"); }} className="bg-white border border-[#c9cccf] text-[#1a1a1a] py-2 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-[#f6f7f8]">
                            <Copy size={16}/> Copy Text
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShareModal;
